import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  AuthenticationResponseJSON,
  RegistrationResponseJSON,
} from '@simplewebauthn/typescript-types';

import httpService from '../http/http.service';

// Interface for WebAuthn backend responses with optional log attribute
interface WebAuthnTelemetryResponse {
  log?: string[];
}

interface RegistrationOptionsResponse
  extends PublicKeyCredentialCreationOptionsJSON,
    WebAuthnTelemetryResponse {}
interface AuthenticationOptionsResponse
  extends PublicKeyCredentialRequestOptionsJSON,
    WebAuthnTelemetryResponse {}
interface VerificationResponse extends WebAuthnTelemetryResponse {
  verified: boolean;
}
interface TransactionVerificationResponse extends WebAuthnTelemetryResponse {
  verified: boolean;
  signatureValid: boolean;
}

export const requestRegistrationOptions = async (
  username: string
): Promise<RegistrationOptionsResponse> => {
  const response = await httpService.post('/generate-registration-options', {
    username,
  });

  return response;
};

export const requestAuthenticationOptions = async (
  username: string
): Promise<AuthenticationOptionsResponse> => {
  const response = await httpService.post('/generate-authentication-options', {
    username,
  });

  return response;
};

export const requestCredentialIDAuthenticationOptions = async (
  username: string,
  tx: string
): Promise<AuthenticationOptionsResponse> => {
  const response = await httpService.post(
    '/generate-authentication-options-credentialid',
    {
      username,
      tx,
    }
  );

  return response;
};

export const requestLargeBlobAuthenticationOptions = async (
  username: string,
  tx: string
): Promise<AuthenticationOptionsResponse> => {
  const response = await httpService.post(
    '/generate-authentication-options-largeblob',
    {
      username,
      tx,
    }
  );

  return response;
};

export const requestSecondCredentialAuthenticationOptions = async (
  username: string,
  tx: string
): Promise<AuthenticationOptionsResponse> => {
  const response = await httpService.post(
    '/generate-authentication-options-second-credential',
    {
      username,
      tx,
    }
  );

  return response;
};

export const verifyRegistration = async (
  username: string,
  response: RegistrationResponseJSON
): Promise<VerificationResponse> => {
  const verifyResponse = await httpService.post('/verify-registration', {
    username,
    response,
  });

  return verifyResponse;
};

export const verifyAuthentication = async (
  username: string,
  response: AuthenticationResponseJSON
): Promise<VerificationResponse> => {
  const verifyResponse = await httpService.post('/verify-authentication', {
    username,
    response,
  });

  return verifyResponse;
};

export const verifyTransaction = async (
  username: string,
  response: AuthenticationResponseJSON
): Promise<TransactionVerificationResponse> => {
  const verifyResponse = await httpService.post('/verify-transaction', {
    username,
    response,
  });

  return verifyResponse;
};

export const verifyTransactionLargeBlob = async (
  username: string,
  response: AuthenticationResponseJSON
): Promise<TransactionVerificationResponse> => {
  const verifyResponse = await httpService.post(
    '/verify-transaction-largeblob',
    {
      username,
      response,
    }
  );

  return verifyResponse;
};

export const verifyTransactionSecondCredential = async (
  username: string,
  response: AuthenticationResponseJSON
): Promise<TransactionVerificationResponse> => {
  const verifyResponse = await httpService.post(
    '/verify-transaction-second-credential',
    {
      username,
      response,
    }
  );

  return verifyResponse;
};
